import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPasswordDialog({ open, onOpenChange, defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/password/request", {
        email,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch {
      // API always returns ok=true, so this shouldn't normally fire
      toast.error("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = (v) => {
    if (!v) {
      setSent(false);
      setEmail(defaultEmail);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="forgot-password-dialog"
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            {sent ? "Check your email" : "Reset your password"}
          </DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-[#1E1E1E] border border-[#27272A]">
              <CheckCircle2 className="h-5 w-5 text-[#3B82F6] mt-0.5 shrink-0" />
              <div className="text-sm text-[#D1D5DB]">
                If <b className="text-[#F3F4F6]">{email}</b> matches an account, we've sent a reset link. Check your inbox — the link expires in 2 hours.
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={() => handleClose(false)}
                data-testid="forgot-password-done-btn"
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              >
                Got it
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reset-email" className="text-xs text-[#9CA3AF]">Email</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <Input
                  id="reset-email"
                  data-testid="forgot-password-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl pl-9"
                />
              </div>
              <p className="text-[11px] text-[#6B7280]">
                We'll email you a secure link to choose a new password.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                onClick={() => handleClose(false)}
                variant="ghost"
                className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || !email}
                data-testid="forgot-password-submit-btn"
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : "Send reset link"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
